import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { StudentEngagementService } from "../student.engagement.service";
import { S25Util } from "../../../util/s25-util";
import { S25EditableModule } from "../../s25-editable/s25.editable.module";
import { Item } from "../../../pojo/Item";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { OrganizationMicroService } from "../../../services/organization.micro.service";
import { Bind } from "../../../decorators/bind.decorator";
import { SeEditableLogo } from "../se.editable.logo.component";
import { Attribute } from "../../s25-event/EventMicroI";

@TypeManagerDecorator("se-about-org")
@Component({
    selector: "se-about-org",
    template: `
        <se-editable-logo
            class="org-image-container"
            [itemId]="orgId()"
            [itemTypeId]="orgTypeId"
            [imageAttr]="imageAttr()"
            [canEdit]="canEdit()"
        ></se-editable-logo>
        @if (errorMsg()) {
            <p class="ngRed">{{ errorMsg() }}</p>
        }
        <div class="org-content">
            @if (mission()) {
                <div class="mission-section">
                    <h3>Our Mission</h3>
                    <s25-ng-editable-textarea
                        [val]="missionInput()"
                        [readOnly]="!canEdit()"
                        [hasCommit]="true"
                        [onCommit]="updateMissionStatement"
                        (valChange)="missionInput.set($event)"
                    ></s25-ng-editable-textarea>
                </div>
            }

            @if (description()) {
                <div class="description-section">
                    <h3>About Us</h3>
                    <p class="description-text">{{ description() }}</p>
                </div>
            }

            @if (website()) {
                <div class="website-section">
                    <h3>Website</h3>
                    <p class="website-text">
                        <a [href]="website()" target="_blank" rel="noopener noreferrer">{{ website() }}</a>
                    </p>
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            display: flex;
            gap: 2rem;
            align-items: flex-start;
        }

        .org-content {
            flex: 1;
            text-align: left;
        }

        .org-content h3 {
            color: #333;
            font-size: 1.3rem;
            margin: 0 0 0.75rem 0;
            font-weight: 600;
        }

        .mission-section,
        .description-section,
        .website-section {
            margin-bottom: 1.5rem;
        }

        .description-text,
        .website-text {
            color: #555;
            line-height: 1.6;
            margin: 0;
        }

        .website-text a {
            color: #2196f3;
            text-decoration: none;
            font-weight: 500;
        }

        .website-text a:hover {
            text-decoration: underline;
        }

        ::ng-deep .mission-section s25-ng-editable-textarea textarea {
            width: 100%;
        }

        ::ng-deep .mission-section s25-ng-editable-textarea .aw-button-group {
            width: fit-content;
            margin-left: auto;
        }

        ::ng-deep .mission-section s25-ng-editable-textarea .aw-button--outline {
            margin-right: 10px;
        }

        ::ng-deep .form-content s25-ng-editable-text .c-input {
            width: 100%;
        }

        @media (max-width: 768px) {
            :host {
                flex-direction: column;
                gap: 1.5rem;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25EditableModule, SeEditableLogo],
})
export class SeAboutOrgComponent {
    orgId = input.required<number>();
    mission = input<string>();
    imageAttr = input<Attribute>();
    description = input<string>();
    website = input<string>();

    private seService = inject(StudentEngagementService);

    protected canEdit = computed(() => this.seService.hasEditRights());
    orgTypeId = Item.Ids.Organization;

    missionAttrId = CustomAttributes.system.orgMission.id;
    missionInput = linkedSignal(() => this.mission());
    errorMsg = signal<string>("");

    @Bind
    async updateMissionStatement() {
        const payload = {
            id: this.orgId(),
            attributes: [{ attributeId: this.missionAttrId, value: this.missionInput() }],
        };
        const [_, error] = await S25Util.Maybe(OrganizationMicroService.saveOrganization(payload));
        if (error) {
            this.errorMsg.set(S25Util.errorText(error));
            console.error(error);
        }
        this.errorMsg.set("");
    }
}
